import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';

interface AlertOptions {
  message: string;
  title?: string;
  icon: 'success' | 'error' | 'warning' | 'info' | 'question';
  confirmButtonText?: string;
  onClose?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  async showAlert({
    message,
    title,
    icon,
    confirmButtonText = 'OK',
    onClose,
  }: AlertOptions): Promise<void> {
    await Swal.fire({
      icon,
      title:
        title ||
        (icon === 'success'
          ? 'Success'
          : icon === 'error'
          ? 'Error'
          : icon === 'warning'
          ? 'Warning'
          : 'Info'),
      text: message,
      confirmButtonText,
      confirmButtonColor: '#07073C',
    }).then(() => {
      if (onClose) {
        onClose();
      }
    });
  }

  success(
    message: string,
    onClose?: () => void,
    title?: string
  ): Promise<void> {
    return this.showAlert({
      message,
      icon: 'success',
      title,
      onClose,
    });
  }

  error(message: string, onClose?: () => void, title?: string): Promise<void> {
    return this.showAlert({
      message,
      icon: 'error',
      title,
      onClose,
    });
  }

  warning(
    message: string,
    onClose?: () => void,
    title?: string
  ): Promise<void> {
    return this.showAlert({
      message,
      icon: 'warning',
      title,
      onClose,
    });
  }

  info(message: string, onClose?: () => void, title?: string): Promise<void> {
    return this.showAlert({
      message,
      icon: 'info',
      title,
      onClose,
    });
  }

  confirm(
    message: string,
    title?: string,
    confirmButtonText = 'Yes',
    cancelButtonText = 'No'
  ): Promise<boolean> {
    return new Promise((resolve) => {
      Swal.fire({
        title: title || 'Confirmation',
        text: message,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
      }).then((result) => {
        resolve(result.isConfirmed);
      });
    });
  }

  toast(
    message: string,
    icon: 'success' | 'error' | 'warning' | 'info' = 'info'
  ): void {
    const Toast = Swal.mixin({
      toast: true,
      position: 'top',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      },
    });

    Toast.fire({
      icon,
      title: message,
    });
  }

  successToast(message: string): void {
    this.toast(message, 'success');
  }

  errorToast(message: string): void {
    this.toast(message, 'error');
  }

  warningToast(message: string): void {
    this.toast(message, 'warning');
  }

  infoToast(message: string): void {
    this.toast(message, 'info');
  }
}
